/**
 * The named trackers both PC surfaces call, in one place (desktop slice 11i).
 *
 * WHAT THIS REPLACES. `apps/extension/src/shared/analytics.ts` and
 * `apps/web/src/services/analytics.ts` declared eleven functions under the same names. Six
 * were code-identical — five byte-identical including their doc comments, and
 * `trackClipComposerSent` differed only in the word "extension"/"web" inside a comment. Three
 * more differed ONLY in which `StorageAdapter` identifier they named. Two are genuinely
 * host-specific and stay with their hosts: `getAnalytics` (surface name and app-version
 * source) and the extras each surface owns.
 *
 * WHY IT MATTERS MORE THAN THE LINE COUNT. `trackSeenBatch` (`seenBatch.ts`) already lived
 * here, and its header states the reason: keeping the whole split-and-count shared "means the
 * organic metrics and the First Light delivery metric cannot drift between extension and web."
 * That argument was never specific to seen-batch. The drift it warns about is already
 * observable elsewhere in this module pair — `pairing_completed` carries `intent` from web and
 * not from the extension, so a breakdown by that property drops extension pairings into
 * `(not set)` rather than into a bucket. This slice does NOT change that (a move must not
 * smuggle a behaviour change); it removes the conditions that let it happen unnoticed.
 *
 * SHAPE. `(adapter, analytics)`-first, copied from `seenBatch.ts` rather than invented — the
 * host owns both, because `getAnalytics()` is lazily constructed per surface and the adapter
 * is `browser.storage.local` on one side and idb-keyval on the other. Functions that need
 * neither take only what they use.
 *
 * FAIL-OPEN LIVES HERE, not at call sites. Analytics must never break a UI action
 * (event-spec rule 8), and a host that forgets its own try/catch must still be safe.
 *
 * PRIVACY. Every property below is a scalar the event registry declares. No notification
 * content, no app package names, no labels — see `.claude/rules/security.md` and the
 * `posthog-instrumentation` skill. `withCommandAnalytics` sends a command's static `id` and
 * deliberately never its label.
 */

import type { StorageAdapter } from "../storage/adapter";
import type { ReflectoAnalytics } from "./client";
import { bumpCounter, captureOnce } from "./state";

/**
 * Funnel anchor: the pairing code is on screen, and the `time_to_pair` clock starts.
 *
 * The anchor write is fire-and-forget on purpose — the capture must not wait on storage, and a
 * failed anchor costs one timing datapoint, not the event.
 */
export function trackPairingCodeDisplayed(
  adapter: StorageAdapter,
  analytics: ReflectoAnalytics,
  now = Date.now()
): void {
  analytics.capture("pairing_code_displayed");
  void setPairCodeShownAnchor(adapter, now).catch(() => {});
}

async function setPairCodeShownAnchor(adapter: StorageAdapter, now: number): Promise<void> {
  const { analyticsState } = await adapter.get("analyticsState");
  await adapter.set({
    analyticsState: { ...(analyticsState ?? {}), pairCodeShownAt: now },
  });
}

/**
 * Consumes the `pairCodeShownAt` anchor (so a re-entrant confirm cannot double-count), aliases
 * this person to the phone (event-spec §1), and writes the durable `pairedAt`.
 *
 * ⚠️ `intent` is optional and the key is omitted entirely when it is `undefined` — NOT
 * defaulted to `"mirror"` here. That is what keeps this a move: web passes a value and the
 * extension passes none, exactly as before. Defaulting inside would silently start emitting a
 * property the extension has never sent, which is a real (and arguably correct) change, but a
 * change — it belongs in its own commit with its own reasoning.
 *
 * ONE THING HERE IS NOT A PURE MOVE, and it is recorded rather than reverted. Both hosts called
 * `Date.now()` TWICE — once for `pairedAt`, once inside the `time_to_pair_s` arithmetic — so a
 * clock tick between the two reads could put the stored timestamp and the reported duration a
 * millisecond out of step with each other. This reads it once and uses that single value for
 * both. Restoring the second read to make the diff literally byte-identical would reinstate a
 * (tiny, real) inconsistency to preserve a claim about the diff, which is the wrong trade.
 */
export async function trackPairingCompleted(
  adapter: StorageAdapter,
  analytics: ReflectoAnalytics,
  intent?: "mirror" | "assistant",
  now = Date.now()
): Promise<void> {
  try {
    const { analyticsState, androidDeviceId } = await adapter.get(
      "analyticsState",
      "androidDeviceId"
    );
    const shownAt = analyticsState?.pairCodeShownAt;
    if (shownAt === undefined) return;
    const rest = { ...(analyticsState ?? {}) };
    delete rest.pairCodeShownAt;
    rest.pairedAt = now;
    await adapter.set({ analyticsState: rest });
    analytics.capture("pairing_completed", {
      time_to_pair_s: Math.round((now - shownAt) / 1000),
      ...(intent === undefined ? {} : { intent }),
    });
    if (androidDeviceId) analytics.aliasToPhone(androidDeviceId);
  } catch {
    // fail open
  }
}

/** Glance open: recurring intent event + counter + first-view milestone (§4.2). */
export function trackGlanceViewed(adapter: StorageAdapter, analytics: ReflectoAnalytics): void {
  analytics.capture("glance_viewed");
  void bumpCounter(adapter, "glance_viewed_count");
  void captureOnce(adapter, analytics, "first_glance_viewed");
}

/** Download CTA = the on-device-AI consent gesture (§4.1). */
export function trackGlanceDownloadConsented(
  adapter: StorageAdapter,
  analytics: ReflectoAnalytics
): void {
  void captureOnce(adapter, analytics, "glance_download_consented");
}

/** "Verify encryption" card open: recurring intent, no properties (§4.2). */
export function trackSafetyNumberViewed(analytics: ReflectoAnalytics): void {
  analytics.capture("safety_number_viewed");
}

/** Clipboard destination becomes active: recurring intent, no properties (spec §4.4). */
export function trackClipboardManagerOpened(analytics: ReflectoAnalytics): void {
  analytics.capture("clipboard_manager_opened");
}

/** `direction` is the only property — never the clip's content or its peer's label. */
export function trackClipCopiedFromHistory(
  analytics: ReflectoAnalytics,
  direction: "sent" | "received"
): void {
  analytics.capture("clip_copied_from_history", { direction });
}

/**
 * Composer send reaches at least one device. `target_scope` is always `"all"` on both PC
 * surfaces — v1 has no per-device send targeting there (spec §7), unlike Android.
 *
 * Since the 2026-08-03 clipboard-screen redesign (R2) the composer is the ONLY send path on
 * these surfaces, so this covers every send. The retired `clip_quick_sent` used to carry the
 * standalone-button share — expect a one-off volume step, not a regression. See
 * `docs/analytics/event-spec.md`.
 */
export function trackClipComposerSent(analytics: ReflectoAnalytics): void {
  analytics.capture("clip_composer_sent", { target_scope: "all" });
}

/**
 * Wraps each palette command's `run` to capture `palette_command_executed` with its static id —
 * `command_id` is the ONLY property (event-spec §4.2), never a label or content. Fail-open: a
 * throwing capture must never block the command itself.
 *
 * Sibling of `capturePaletteOpened`, which has lived in `state.ts` since the palette shipped.
 * That split — one half shared, the other copied into both hosts — is what this slice closes.
 *
 * GENERIC RATHER THAN TYPED TO `PaletteCommand`, and that is a package-boundary fact, not a
 * style choice: `PaletteCommand` is declared in `@reflecto/client-ui`, which DEPENDS ON this
 * package. Importing it here would invert the dependency. The generic also keeps each host's
 * own `PaletteCommand` as the return type, so neither call site needs a cast — a structural
 * `{ id, run }[]` parameter would have widened both.
 */
export function withCommandAnalytics<T extends { id: string; run: () => void }>(
  analytics: ReflectoAnalytics,
  commands: T[]
): T[] {
  return commands.map((cmd) => ({
    ...cmd,
    run: () => {
      try {
        analytics.capture("palette_command_executed", { command_id: cmd.id });
      } catch {
        // fail open — analytics must never block a command
      }
      cmd.run();
    },
  }));
}
